import {
  Box,
  Center,
  Clickable,
  Heading,
  HStack,
  Icon,
  Spinner,
  Text,
  ThemeColorTextPath,
  VStack,
} from 'platform/foundation';
import {css} from 'styled-components';
import 'swiper/css';
import {Autoplay} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';

import {ReactNode, Suspense, use} from 'react';

type LazyImageImport = Promise<{default: string}>;

const BANNERS = {
  bgRemoval: () => import('./assets/backgroundRemoval.png') as LazyImageImport,
  taskManagement: () => import('./assets/taskManagement.png') as LazyImageImport,
  digitalSignature: () => import('./assets/digitalSignature.png') as LazyImageImport,
  aiDescription: () => import('./assets/aiDescription.png') as LazyImageImport,
  chat: () => import('./assets/chat.png') as LazyImageImport,
} as const;

const AUTOPLAY_DELAY_IN_MS = 7000;

export interface AdBannerItem {
  headingColor?: ThemeColorTextPath;
  subheadingColor?: ThemeColorTextPath;
  bgImage: keyof typeof BANNERS;
  heading: string;
  subheading?: string;
  onClick: () => void;
}

export interface AdBannerProps {
  items: AdBannerItem[];
}

const Background = ({children, bgImage}: {children: ReactNode; bgImage: LazyImageImport}) => {
  const image = use(bgImage);

  return (
    <div
      css={css`
        background-image: url(${image.default});
        background-size: cover;
        border-radius: ${({theme}) => theme.getSize(2)};
        width: 100%;
        min-height: ${({theme}) => theme.getSize(55)};
        padding: ${({theme}) => theme.getSize(5)};
      `}
    >
      {children}
    </div>
  );
};

function Slide(props: AdBannerItem) {
  const headingColor = props.headingColor ?? 'primary';
  const subheadingColor = props.subheadingColor ?? 'secondary';

  return (
    <Clickable onClick={props.onClick}>
      <Background bgImage={BANNERS[props.bgImage]?.()}>
        <HStack>
          <VStack spacing={2} maxWidth={58}>
            <Heading size={4} color={headingColor}>
              {props.heading}
            </Heading>
            <Text size="xSmall" color={subheadingColor}>
              {props.subheading}
            </Text>
          </VStack>
          <Box width={10} height={10} borderRadius="circular" backgroundColor="general.white">
            <Center height="100%">
              <Icon value="navigation/arrow_forward_ios" size={4} />
            </Center>
          </Box>
        </HStack>
      </Background>
    </Clickable>
  );
}

export function AdBanner(props: AdBannerProps) {
  return (
    <Suspense
      fallback={
        <Center height={55} width={80}>
          <Spinner />
        </Center>
      }
    >
      <div
        css={css`
          width: 100%;

          // not ideal, but to keep the original behaviour, fixing width/height to image w/h
          @media (min-width: 1025px) {
            max-width: ${({theme}) => theme.getSize(80)};
            max-height: ${({theme}) => theme.getSize(55)};
          }
        `}
      >
        <Swiper
          modules={[Autoplay]}
          autoplay={{delay: AUTOPLAY_DELAY_IN_MS, disableOnInteraction: false}}
          loop
        >
          {props.items.map((item) => (
            <SwiperSlide key={item.bgImage}>
              <Slide {...item} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </Suspense>
  );
}
